import moment from 'moment';
import { browserHistory } from 'react-router';
import FetchService from '../../../core/services/FetchService';
import FormService from '../../../core/services/FormService';
import NotificationService from '../../../core/services/NotificationService';
import ContractProductsService from './ContractProductsService';
import { isCNPJ, isCPF, isRequired, isValidEmail, minLength } from '../../../core/validations';
import { get, pickBy, omit, pick } from 'lodash';
import { BRAZIL, CONTRACT_PRODUCTS } from '../../../core/consts';
import { extendObservable, toJS } from 'mobx';

class ContractFormService {
  fetch = new FetchService();
  submit = new FetchService();
  form = new FormService();
  productsService = new ContractProductsService();

  constructor() {
    extendObservable(this, {
      isV2: false,
      type: '',
      contractId: '',
    });
    this.form.validations = {
      country: [isRequired],
      state: [isRequired],
      documentNumber: [isRequired],
      socialReason: [isRequired],
      address: [isRequired],
      district: [isRequired],
      number: [isRequired],
      zipCode: [isRequired],
      emails: [isRequired, isValidEmail],
      startsAt: [isRequired],
      dueDay: [isRequired],
      type: [isRequired],
      allowedPaymentMethods: [isRequired, minLength(1)],
      companyName: [isRequired],
      companySegment: [isRequired],
      hubspotDealId: [isRequired],
    };
  }

  onChange = (path, value) => {
    if (path === 'isDistributor') {
      if (value) {
        this.form.validations.distributor = [isRequired];
        this.form.validations.companyName = [];
        this.form.validations.companySegment = [];
      } else {
        this.form.validations.distributor = [];
        this.form.validations.companyName = [isRequired];
        this.form.validations.companySegment = [isRequired];
      }
    }
    if (path === 'company') {
      if (value) {
        this.form.validations.companyName = [];
        this.form.validations.companySegment = [];
      } else {
        this.form.validations.companyName = [isRequired];
        this.form.validations.companySegment = [isRequired];
      }
    }
    if (path === 'contractFile') {
      this.form.setValue('contractUrl', null);
    }
    if (path === 'documentNumber' && BRAZIL.includes(get(this.form.getValues(), 'country', ''))) {
      if (get(this.form.getValues(), 'documentNumber', '').length > 11) {
        this.form.validations.documentNumber = [isRequired, isCNPJ];
      } else {
        this.form.validations.documentNumber = [isRequired, isCPF];
      }
    }
    if (path === 'country' && !BRAZIL.includes(value)) {
      this.form.validations.documentNumber = [isRequired];
    }
    if (path === 'type') {
      if (this.contractId) {
        return;
      }
      this.form.setValue(path, value);
      this.productsService.type = value;
      if (value === 'UPFRONT') {
        this.form.setValue('startsAt', null);
        this.form.setValue('endsAt', null);
        this.form.setValue('dueDay', null);

        this.form.validations.startsAt = [];
        this.form.validations.dueDay = [];
      } else {
        this.form.validations.startsAt = [isRequired];
        this.form.validations.dueDay = [isRequired];
      }
      this.productsService.init(value);
      this.productsService.load();
    } else {
      this.form.setValue(path, value);
    }
  };

  init = () => {
    this.form.setInitialValues({});
    this.form.reset();
    this.contractId = null;
    this.productsService.init();
  };

  load = async (contractId) => {
    this.contractId = contractId;
    await this.fetch.fetch({ url: `/contracts/${this.contractId}`, apiVersion: 'v2' });
    if (this.fetch.data) {
      this.type = this.fetch.data.type;
      this.isV2 = this.fetch.data.version === 'v2';

      this.form.setInitialValues({
        ...pickBy(this.fetch.data, (value) => value !== null && value !== undefined),
        startsAt: moment(this.fetch.data.startsAt).utc(),
        ...(this.fetch.data.endsAt && { endsAt: moment(this.fetch.data.endsAt).utc() }),
      });
      if (this.fetch.data.isDistributor || this.fetch.data.company) {
        this.form.validations.companyName = [];
        this.form.validations.companySegment = [];
      }
      if (this.fetch.data.hubspotDealId) {
        this.form.setValue('hasHubspotDealId', true);
      }
      if (this.fetch.data.status === 'FREE_TRIAL') {
        this.form.validations.hubspotDealId = [];
      }
      if (BRAZIL.includes(this.fetch.data.country)) {
        if (this.fetch.data.documentNumber.length > 11) {
          this.form.validations.documentNumber = [isRequired, isCNPJ];
        } else {
          this.form.validations.documentNumber = [isRequired, isCPF];
        }
      } else {
        this.form.validations.documentNumber = [isRequired];
      }

      this.productsService.init(this.fetch.data.type, !!contractId);
      this.productsService.isV2 = this.fetch.data.version === 'v2';
      this.productsService.contractProducts = this.fetch.data.products.map((product) => ({
        ...product,
        rowKey: product.id,
        ...(product.beginningOfTerm && { beginningOfTerm: moment(product.beginningOfTerm).add(3, 'hours') }),
        ...(this.fetch.data.type === 'UPFRONT' && {
          isEnabled: true,
        }),
        description: product.description,
      }));
      await this.productsService.load();
    }
  };

  handleSubmit = () => {
    this.form.submitted = true;
    if (this.form.errors) {
      NotificationService.addNotification('Fill the required fields', 'error');
      return;
    }
    if (!this.productsService.contractProducts.length) {
      NotificationService.addNotification('At least one product must be registered to create the contract.', 'error');
      return;
    }

    const formValues = this.contractId
      ? pick(this.form.getValues(), [
          'documentNumber',
          'socialReason',
          'country',
          'state',
          'city',
          'address',
          'district',
          'number',
          'contactBirthDate',
          'contactGender',
          'zipCode',
          'emails',
          'phone',
          'allowedPaymentMethods',
          'endsAt',
          'dueDay',
          'contactName',
          'hubspotDealId',
        ])
      : this.form.getValues();

    const payload = {
      ...omit(formValues, ['status', 'deleted', 'createdAt', 'updatedAt', 'id']),
      ...(formValues.startsAt && {
        startsAt: moment(formValues.startsAt).utc().startOf('month').toISOString(),
      }),
      ...(formValues.endsAt && {
        endsAt: moment(formValues.endsAt).utc().endOf('day').toISOString(),
      }),
      ...(formValues.contactBirthDate && {
        contactBirthDate: moment(formValues.contactBirthDate).utc().startOf('day').toISOString(),
      }),
      ...(!!this.productsService.contractProducts && {
        products: this.productsService.contractProducts
          .filter((product) => {
            if (
              product.hs_sku !== CONTRACT_PRODUCTS.OTHERS &&
              (formValues.type === 'UPFRONT' || this.type === 'UPFRONT')
            ) {
              return product.isEnabled;
            }
            return true;
          })
          .map((product) => ({
            ...omit(product, ['rowKey', 'id', 'group']),
            _id: product.id, //since backend uses _id, we must send it back to avoid change the value
            description: product.description,
            hs_sku: product.hs_sku,
            amount: product.amount ? Number(product.amount) : undefined,
            freeAmount: product.freeAmount ? Number(product.freeAmount) : undefined,
            finalUnitPrice: product.finalUnitPrice ? Number(product.finalUnitPrice) : 0,
            installments: product.installments ? Number(product.installments) : undefined,
            paidInstallments: product.paidInstallments ? Number(product.paidInstallments) : undefined,
            finalUnitPriceRanges: product.finalUnitPriceRanges ? toJS(product.finalUnitPriceRanges) : undefined,
          })),
      }),
    };

    let bodyData;
    if (this.form.getValue('contractFile')) {
      const formData = new FormData();
      const appendToFormData = (data, parentKey = '') => {
        if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
          if (Array.isArray(data)) {
            data.forEach((item, index) => {
              appendToFormData(item, `${parentKey}[${index}]`);
            });
          } else {
            Object.keys(data).forEach((key) => {
              appendToFormData(data[key], parentKey ? `${parentKey}[${key}]` : key);
            });
          }
        } else if (data !== null && data !== undefined) {
          formData.append(parentKey, data);
        }
      };
      appendToFormData(payload);
      formData.append('contractFile', this.form.getValue('contractFile'));

      bodyData = formData;
    } else {
      bodyData = payload;
    }

    this.submit
      .fetch({
        method: this.contractId ? 'put' : 'post',
        url: this.contractId ? `/contracts/${this.contractId}` : `/contracts`,
        apiVersion: this.contractId && !this.isV2 ? 'v2' : 'v3',
        body: bodyData,
      })
      .then(() => {
        if (this.submit.data) {
          NotificationService.addNotification(
            `Contract ${this.contractId ? 'updated' : 'created'} successfully.`,
            'success'
          );
          browserHistory.push('/contracts');
        }
        if (this.submit.error) {
          if (this.submit.response.data.error === 'existent_contract_document_number') {
            NotificationService.addNotification(
              'A contract with this document number already exists. Please, choose another document number to create a new contract.',
              'error'
            );
          } else {
            NotificationService.addNotification(
              this.submit.error || `Error ${this.contractId ? 'updating' : 'creating'} Contract.`,
              'error'
            );
          }
        }
      });
  };
}

const contractFormService = new ContractFormService();

export default contractFormService;
